import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import axios from "axios"; // ✅ PERBAIKAN: Import axios secara statis di sini
import {
    Loader2, ArrowLeft, Copy, CheckCircle2,
    Clock, Receipt, XCircle,
    BookOpen, Wallet, ShieldCheck, Hourglass, Timer, ListChecks, Send, AlertCircle, X, Sun, Moon, Info,
    AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { axiosClient } from "@/lib/axiosClient";
import PaymentGuide from "@/components/public/conference-payment/PaymentGuide";
import { motion, AnimatePresence } from "framer-motion";

interface TokenInvoiceResponse {
    status: string;
    data: {
        invoice_number: string;
        customer_number: string | null;
        status: "unpaid" | "paid" | "expired" | "refunded";
        verification_status: "not_required" | "pending" | "approved" | "rejected";
        amount: string;
        currency: string;
        expired_at: string | null;
        participant_name: string;
        email: string;
        institution_name: string;
        is_presenter: boolean;
        submission_title: string | null;
        submission_authors: string | null;
        presentation_format: string | null;
        package_name: string;
        conference_name: string;
        quantity: number;
    };
}

interface LaravelErrorResponse {
    message: string;
    errors?: Record<string, string[]>;
}

// ✅ PERBAIKAN: Fungsi parseLaravelError kini menggunakan axios import statis
const parseLaravelError = (error: unknown): string => {
    if (axios.isAxiosError<LaravelErrorResponse>(error)) {
        const data = error.response?.data;
        if (data?.errors) {
            const firstErrorKey = Object.keys(data.errors)[0];
            return data.errors[firstErrorKey][0];
        }
        return data?.message || "Terjadi kesalahan sistem. Silakan coba lagi.";
    }
    if (error instanceof Error) return error.message;
    return "Terjadi kesalahan yang tidak diketahui.";
};

export default function ConferenceInvoicePage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    // Tangkap status email_sent dari halaman sebelumnya
    const initialEmailSent = location.state?.emailSent;

    const [copied, setCopied] = useState(false);
    const [timeLeft, setTimeLeft] = useState<string>("");

    // State Smart Banner untuk menenangkan User (Registration Success + Email Status)
    const [bannerState, setBannerState] = useState<"created_success" | "created_failed" | "resend_success" | null>(
        initialEmailSent === true ? "created_success" :
            initialEmailSent === false ? "created_failed" : null
    );

    // State untuk Dark Mode
    const [isDark, setIsDark] = useState<boolean>(false);

    // Toast Notification State
    const [toast, setToast] = useState<{ message: string; type: "error" | "success" } | null>(null);

    const showToast = (message: string, type: "error" | "success" = "error") => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 5000);
    };

    // Sync Dark Mode
    useEffect(() => {
        const savedTheme = localStorage.getItem("theme");
        if (savedTheme === "dark") {
            setIsDark(true);
            document.documentElement.classList.add("dark");
        } else {
            setIsDark(false);
            document.documentElement.classList.remove("dark");
        }
    }, []);

    // Fungsi Toggle Dark Mode
    const toggleDarkMode = () => {
        const newMode = !isDark;
        setIsDark(newMode);
        if (newMode) {
            document.documentElement.classList.add("dark");
            localStorage.setItem("theme", "dark");
        } else {
            document.documentElement.classList.remove("dark");
            localStorage.setItem("theme", "light");
        }
    };

    const { data: invoiceData, isLoading, isError } = useQuery({
        queryKey: ["invoice", token],
        queryFn: async () => {
            const response = await axiosClient.get<TokenInvoiceResponse>(`/conference-billing/invoice/token/${encodeURIComponent(token || "")}`);
            return response.data.data;
        },
        enabled: !!token,
        retry: 1,
        refetchInterval: (query) => {
            const data = query.state.data as TokenInvoiceResponse['data'] | undefined;
            return data?.verification_status === 'pending' ? 10000 : false;
        },
    });

    // --- MUTASI RESEND EMAIL ---
    const resendEmailMutation = useMutation({
        mutationFn: async () => {
            if (!invoiceData) throw new Error("Invoice data not loaded");

            const formData = new FormData();
            formData.append("invoice_number", invoiceData.invoice_number);
            formData.append("email", invoiceData.email);

            const response = await axiosClient.post("/conference-billing/invoice/resend-email", formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        },
        onSuccess: (data) => {
            showToast(data.message || "Email notifikasi berhasil dikirim ulang!", "success");
            // Ubah status banner menjadi sukses setelah resend terkirim
            setBannerState("resend_success");
        },
        onError: (error) => {
            showToast(parseLaravelError(error), "error");
        }
    });

    // --- LOGIKA WAKTU MUNDUR (COUNTDOWN) ---
    useEffect(() => {
        if (!invoiceData?.expired_at || invoiceData.status !== "unpaid") return;

        const calculateTimeLeft = () => {
            const difference = new Date(invoiceData.expired_at!).getTime() - new Date().getTime();
            if (difference <= 0) return "Expired";

            const days = Math.floor(difference / (1000 * 60 * 60 * 24));
            const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
            const minutes = Math.floor((difference / 1000 / 60) % 60);
            const seconds = Math.floor((difference / 1000) % 60);

            let timeString = "";
            if (days > 0) timeString += `${days}d `;
            timeString += `${hours.toString().padStart(2, "0")}h ${minutes.toString().padStart(2, "0")}m ${seconds.toString().padStart(2, "0")}s`;
            return timeString;
        };

        setTimeLeft(calculateTimeLeft());
        const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
        return () => clearInterval(timer);
    }, [invoiceData]);

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const formatCurrency = (amount: string | number, currency: string) => {
        return new Intl.NumberFormat(currency === "USD" ? "en-US" : "id-ID", {
            style: "currency",
            currency: currency,
            minimumFractionDigits: 0,
        }).format(Number(amount));
    };

    const formatDate = (isoString: string) => {
        const date = new Date(isoString);
        return new Intl.DateTimeFormat("en-US", {
            day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false
        }).format(date);
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 transition-colors">
                <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-4" />
                <p className="text-slate-500 dark:text-slate-400 font-medium tracking-wide">Retrieving invoice details...</p>
            </div>
        );
    }

    if (isError || !invoiceData) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 p-4 text-center transition-colors">
                <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mb-6">
                    <Receipt className="w-10 h-10" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Invoice Not Found</h1>
                <p className="text-slate-500 dark:text-slate-400 max-w-md mb-8">The payment link you are trying to access is invalid, expired, or has been removed.</p>
                <Button onClick={() => navigate("/conference-payment")} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8 h-12 font-bold">Back to Registration</Button>
            </div>
        );
    }

    const { verification_status, status, currency, amount } = invoiceData;

    // --- LOGIKA KALKULASI ADMIN FEE BEDA BANK ---
    // ✅ Tambahan: Logika fee untuk transaksi di luar BNI
    const adminFee = currency === "USD" ? 5 : 2500;
    const totalWithFee = Number(amount) + adminFee;

    // --- LOGIKA TEMA & IKON HEADER DINAMIS ---
    let headerConfig = {
        bgClass: "bg-blue-600 dark:bg-blue-900",
        statusText: "WAITING PAYMENT",
        Icon: Wallet
    };

    if (verification_status === "pending") {
        headerConfig = { bgClass: "bg-amber-500 dark:bg-amber-600", statusText: "VERIFICATION PENDING", Icon: Hourglass };
    } else if (verification_status === "rejected") {
        headerConfig = { bgClass: "bg-red-600 dark:bg-red-700", statusText: "REJECTED", Icon: XCircle };
    } else if (status === "paid") {
        headerConfig = { bgClass: "bg-emerald-600 dark:bg-emerald-700", statusText: "PAYMENT SUCCESSFUL", Icon: ShieldCheck };
    } else if (status === "expired") {
        headerConfig = { bgClass: "bg-slate-600 dark:bg-slate-800", statusText: "INVOICE EXPIRED", Icon: XCircle };
    }

    return (
        <div className="min-h-screen bg-[#FAF9F6] dark:bg-slate-950 font-sans text-slate-900 dark:text-slate-100 py-8 px-4 transition-colors relative">

            {/* CUSTOM TOAST NOTIFICATION */}
            <AnimatePresence>
                {toast && (
                    <motion.div
                        initial={{ opacity: 0, y: -50, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.9 }}
                        className="fixed top-24 left-1/2 -translate-x-1/2 z-50 px-4 w-full max-w-md"
                    >
                        <div className={`flex items-start gap-3 p-4 rounded-xl shadow-2xl border ${toast.type === 'error' ? 'bg-white border-red-200 text-red-700 dark:bg-slate-900 dark:border-red-900/50 dark:text-red-400' : 'bg-white border-green-200 text-green-700 dark:bg-slate-900 dark:border-green-900/50 dark:text-green-400'}`}>
                            {toast.type === 'error' ? <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" /> : <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />}
                            <p className="text-sm font-medium leading-snug flex-1">{toast.message}</p>
                            <button type="button" onClick={() => setToast(null)} className="opacity-50 hover:opacity-100 transition-opacity">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="max-w-3xl mx-auto space-y-6">

                {/* Navigasi Top Bar (Back Button + Dark Mode Toggle) */}
                <div className="flex items-center justify-between mb-6">
                    <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white transition-colors">
                        <ArrowLeft className="w-4 h-4" /> Back to Home
                    </button>

                    <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="rounded-full w-10 h-10 bg-slate-200/50 hover:bg-slate-300/50 dark:bg-slate-800 dark:hover:bg-slate-700 shrink-0 text-slate-700 dark:text-slate-200 transition-colors">
                        {isDark ? <Sun className="h-4 w-4 text-yellow-400" /> : <Moon className="h-4 w-4" />}
                    </Button>
                </div>

                {/* SMART BANNER: REGISTRATION SUCCESS & EMAIL STATUS */}
                <AnimatePresence>
                    {/* Skenario 1: Semuanya Sukses */}
                    {bannerState === "created_success" && (
                        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-5 rounded-2xl shadow-sm flex items-start gap-4 mb-6 relative">
                            <CheckCircle2 className="w-6 h-6 text-green-500 shrink-0 mt-0.5" />
                            <div className="flex-1 pr-6">
                                <h4 className="text-base font-bold text-green-800 dark:text-green-400">Registration Successful!</h4>
                                <p className="text-sm text-green-700 dark:text-green-300 mt-1 leading-relaxed">
                                    Your invoice has been successfully created. We have also sent a copy to <b>{invoiceData.email}</b>.
                                </p>
                            </div>
                            <button onClick={() => setBannerState(null)} className="absolute top-5 right-5 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </motion.div>
                    )}

                    {/* Skenario 2: Tagihan Sukses Dibuat, TAPI Email Gagal (Dilengkapi Tombol Resend) */}
                    {bannerState === "created_failed" && status === "unpaid" && (
                        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 p-5 rounded-2xl shadow-sm flex items-start gap-4 mb-6 relative">
                            <CheckCircle2 className="w-6 h-6 text-green-500 shrink-0 mt-0.5" />
                            <div className="flex-1 pr-6">
                                <h4 className="text-base font-bold text-orange-800 dark:text-orange-400">Registration Successful, but Email Failed</h4>
                                <p className="text-sm text-orange-700 dark:text-orange-300 mt-1 mb-4 leading-relaxed">
                                    Your invoice is successfully generated, but we couldn't send the automatic notification to <b>{invoiceData.email}</b>. Please save this link or click the button below to resend.
                                </p>
                                <Button
                                    onClick={() => resendEmailMutation.mutate()}
                                    disabled={resendEmailMutation.isPending}
                                    variant="outline"
                                    size="sm"
                                    className="border-orange-300 text-orange-700 hover:bg-orange-100 dark:border-orange-700 dark:text-orange-300 dark:hover:bg-orange-900/50 rounded-xl font-bold"
                                >
                                    {resendEmailMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                                    Resend Email
                                </Button>
                            </div>
                            <button onClick={() => setBannerState(null)} className="absolute top-5 right-5 text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-300 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </motion.div>
                    )}

                    {/* Skenario 3: Transisi Sukses setelah User klik "Resend" */}
                    {bannerState === "resend_success" && status === "unpaid" && (
                        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-5 rounded-2xl shadow-sm flex items-start gap-4 mb-6 relative">
                            <CheckCircle2 className="w-6 h-6 text-green-500 shrink-0 mt-0.5" />
                            <div className="flex-1 pr-6">
                                <h4 className="text-base font-bold text-green-800 dark:text-green-400">Email Sent Successfully</h4>
                                <p className="text-sm text-green-700 dark:text-green-300 mt-1 leading-relaxed">
                                    The invoice has been successfully sent to <b>{invoiceData.email}</b>. Please check your inbox or spam folder.
                                </p>
                            </div>
                            <button onClick={() => setBannerState(null)} className="absolute top-5 right-5 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ========================================================= */}
                {/* KARTU 1: PAYMENT ACTION & STATUS */}
                {/* ========================================================= */}
                <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden">

                    {/* Header Dinamis */}
                    <div className={`p-6 md:p-8 text-white relative overflow-hidden flex items-center gap-4 ${headerConfig.bgClass}`}>
                        <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
                        <div className="relative z-10 bg-white/20 p-3.5 rounded-xl backdrop-blur-sm border border-white/20 shrink-0">
                            <headerConfig.Icon className="w-7 h-7 text-white" strokeWidth={2.5} />
                        </div>
                        <div className="relative z-10 flex-1 flex flex-col md:flex-row md:items-center justify-between gap-2">
                            <div>
                                <p className="text-white/80 text-[10px] font-bold uppercase tracking-widest mb-0.5">Registration Ref.</p>
                                <p className="text-base font-bold tracking-widest">{invoiceData.customer_number || "-"}</p>
                            </div>
                            <div className="md:text-right">
                                <p className="text-white/80 text-[10px] uppercase tracking-wider font-bold mb-0.5">Status</p>
                                <h1 className="text-lg sm:text-xl font-black tracking-tight uppercase drop-shadow-sm">{headerConfig.statusText}</h1>
                            </div>
                        </div>
                    </div>

                    {/* Banner Status untuk Pending & Rejected */}
                    {verification_status === "pending" && (
                        <div className="bg-slate-50/30 dark:bg-slate-900/50 p-8 text-center flex flex-col items-center">
                            <Clock className="w-14 h-14 text-amber-500 mb-4 animate-pulse" />
                            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Review in Progress</h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md leading-relaxed">Your Student ID is currently being reviewed by the committee. Your Virtual Account will be generated here once approved.</p>
                        </div>
                    )}

                    {verification_status === "rejected" && (
                        <div className="bg-slate-50/30 dark:bg-slate-900/50 p-8 text-center flex flex-col items-center">
                            <XCircle className="w-14 h-14 text-red-500 mb-4" />
                            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Verification Failed</h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md leading-relaxed">Your document could not be verified. Please contact the administrator via WhatsApp for further clarification.</p>
                        </div>
                    )}

                    {status === "expired" && (
                        <div className="bg-slate-50/30 dark:bg-slate-900/50 p-8 text-center flex flex-col items-center">
                            <XCircle className="w-14 h-14 text-slate-400 mb-4" />
                            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Invoice Expired</h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md leading-relaxed">The payment time limit has passed. Please register again to get a new Virtual Account.</p>
                        </div>
                    )}

                    {status === "paid" && (
                        <div className="bg-slate-50/30 dark:bg-slate-900/50 p-8 text-center flex flex-col items-center">
                            <div className="w-20 h-20 bg-green-50 dark:bg-green-900/20 text-green-500 rounded-full flex items-center justify-center mb-4">
                                <CheckCircle2 className="w-10 h-10" />
                            </div>
                            <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2">Payment Confirmed!</h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Thank you. We have successfully received your payment and your registration is secured.</p>
                        </div>
                    )}

                    {/* VIRTUAL ACCOUNT SECTION & FEE WARNING */}
                    {(verification_status === "approved" || verification_status === "not_required") && status === "unpaid" && invoiceData.invoice_number && (
                        <div className="flex flex-col">
                            {/* BAGIAN ATAS: TOTAL & VIRTUAL ACCOUNT */}
                            <div className="p-6 md:p-10 bg-slate-50/30 dark:bg-slate-900/50 flex flex-col items-center text-center border-b border-slate-100 dark:border-slate-800">
                                {/* Total Pembayaran */}
                                <div>
                                    <p className="text-slate-500 dark:text-slate-400 text-[11px] font-bold uppercase tracking-widest mb-1">Total Payment</p>
                                    <h2 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white tracking-tighter">
                                        {formatCurrency(invoiceData.amount, invoiceData.currency)}
                                    </h2>

                                    {/* Waktu Mundur & Expired Date (Kapsul Kecil) */}
                                    {invoiceData.expired_at && timeLeft !== "Expired" && (
                                        <div className="mt-3 flex flex-wrap justify-center items-center gap-2 text-xs font-medium text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 py-1.5 px-3.5 rounded-lg border border-orange-100 dark:border-orange-900/50 w-fit mx-auto">
                                            <Timer className="w-3.5 h-3.5 animate-pulse" />
                                            <span>Expires in: <span className="font-bold font-mono tracking-wider">{timeLeft}</span></span>
                                            <span className="opacity-50 hidden sm:inline">•</span>
                                            <span className="hidden sm:inline">Pay before {formatDate(invoiceData.expired_at)}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Kotak Virtual Account (Satu Baris, Tanpa Scrollbar) */}
                                <div className="w-full mt-8 bg-white dark:bg-slate-800 rounded-2xl p-4 md:p-5 border border-slate-200 dark:border-slate-700 shadow-sm relative group transition-colors duration-300 hover:border-blue-300 dark:hover:border-blue-700">
                                    <p className="text-slate-500 dark:text-slate-400 text-[11px] font-bold uppercase tracking-widest mb-3 text-left pl-1">Virtual Account Number</p>

                                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                        {/* overflow-hidden dan flex-1 untuk mencegah offside */}
                                        <div className="flex-1 min-w-0 w-full text-center sm:text-left px-1 overflow-hidden">
                                            <span className="text-2xl md:text-3xl font-black text-blue-700 dark:text-blue-400 font-mono tracking-widest whitespace-nowrap select-all block truncate">
                                                {invoiceData.invoice_number}
                                            </span>
                                        </div>

                                        <Button
                                            onClick={() => handleCopy(invoiceData.invoice_number || "")}
                                            variant="default"
                                            className="rounded-xl h-11 px-6 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm w-full sm:w-auto shrink-0 shadow-md"
                                        >
                                            {copied ? <><CheckCircle2 className="w-4 h-4 mr-2" /> COPIED</> : <><Copy className="w-4 h-4 mr-2" /> COPY NUMBER</>}
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* ✅ BAGIAN BAWAH: INFORMASI TRANSFER CONDITIONAL BERDASARKAN CURRENCY */}
                            <div className="p-6 md:p-10 bg-white dark:bg-slate-900">
                                {currency === "USD" ? (
                                    <div className="space-y-5">
                                        <div className="flex items-center gap-3 text-blue-700 dark:text-blue-400">
                                            <Info className="w-5 h-5" />
                                            <h4 className="font-bold text-base">International / Non-BNI Transfer (USD)</h4>
                                        </div>

                                        <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                                            If you are paying from an overseas bank or a non-BNI account, please wire your payment to the following bank account details:
                                        </p>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm bg-slate-50 dark:bg-slate-800/50 p-5 rounded-2xl border border-slate-100 dark:border-slate-700">
                                            <div>
                                                <p className="text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Account Name</p>
                                                <p className="font-bold text-slate-900 dark:text-white">PANITIA PELAKSANA KONASPI XII</p>
                                            </div>
                                            <div>
                                                <p className="text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Account Number</p>
                                                <p className="font-bold text-blue-600 dark:text-blue-400 font-mono text-base">2023869027 <span className="text-xs text-slate-500 font-sans">(USD)</span></p>
                                            </div>
                                            <div>
                                                <p className="text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Bank Name</p>
                                                <p className="font-bold text-slate-900 dark:text-white">Bank Negara Indonesia (BNI)</p>
                                            </div>
                                            <div>
                                                <p className="text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Swift Code</p>
                                                <p className="font-bold text-slate-900 dark:text-white font-mono">BNINIDJASGR</p>
                                            </div>
                                            <div className="sm:col-span-2">
                                                <p className="text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Bank Address & Branch</p>
                                                <p className="font-bold text-slate-900 dark:text-white">Jalan Ngurah Rai 48, Singaraja, Bali, Indonesia (Singaraja Branch)</p>
                                            </div>
                                            <div className="sm:col-span-2">
                                                <p className="text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Transfer Note / Description</p>
                                                <div className="bg-white dark:bg-slate-900 px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-700 mt-1 inline-block shadow-sm">
                                                    <p className="font-bold text-blue-600 dark:text-blue-400 font-mono text-sm sm:text-base tracking-wide flex items-center gap-2">
                                                        <span className="text-slate-400 select-none">VA:</span> {invoiceData.invoice_number}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Peringatan Important Note USD dengan Simulasi Dinamis */}
                                        <div className="bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/60 dark:border-amber-800/30 rounded-2xl p-5 flex gap-3 shadow-sm">
                                            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                                            <div className="text-sm text-amber-900 dark:text-amber-200/90 w-full">
                                                <p className="font-bold mb-2">Important Note: Wire Transfer Fees</p>
                                                <p className="mb-3">
                                                    Please ensure the transfer is sent as <strong className="font-extrabold text-amber-950 dark:text-amber-100">"Full Amount" (OUR)</strong>. If not sent as Full Amount, an additional bank fee will be applied.
                                                </p>
                                                <div className="bg-white/80 dark:bg-slate-900/50 p-4 rounded-xl border border-amber-100 dark:border-amber-900/50">
                                                    <p className="font-bold text-[10px] text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-widest">Payment Simulation</p>
                                                    <ul className="space-y-1 font-mono text-sm">
                                                        <li className="flex justify-between gap-4">
                                                            <span>Invoice Amount:</span>
                                                            <span>{formatCurrency(amount, currency)}</span>
                                                        </li>
                                                        <li className="flex justify-between gap-4 border-b border-amber-100 dark:border-amber-800/50 pb-1.5">
                                                            <span>Additional Bank Fee:</span>
                                                            <span>{formatCurrency(adminFee, currency)} +</span>
                                                        </li>
                                                        <li className="flex justify-between gap-4 font-bold text-blue-700 dark:text-blue-400 pt-1.5">
                                                            <span>Total to Transfer:</span>
                                                            <span>{formatCurrency(totalWithFee, currency)}</span>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-5">
                                        <div className="flex items-center gap-3 text-emerald-700 dark:text-emerald-400">
                                            <Info className="w-5 h-5" />
                                            <h4 className="font-bold text-base">Informasi Pembayaran (IDR)</h4>
                                        </div>

                                        {/* Peringatan Important Note IDR dengan Simulasi Dinamis */}
                                        <div className="bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/60 dark:border-amber-800/30 rounded-2xl p-5 flex gap-3 shadow-sm">
                                            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                                            <div className="text-sm text-amber-900 dark:text-amber-200/90 w-full">
                                                <p className="font-bold mb-2">Important: Non-BNI Transfer / Pembayaran Beda Bank</p>
                                                <p className="mb-3">
                                                    Jika Anda transfer menggunakan bank <strong>selain BNI</strong>, wajib menginput <strong>Nominal Tagihan Pokok + Fee Admin VA ({formatCurrency(adminFee, currency)})</strong> secara manual.
                                                </p>
                                                <div className="bg-white/80 dark:bg-slate-900/50 p-4 rounded-xl border border-amber-100 dark:border-amber-900/50">
                                                    <p className="font-bold text-[10px] text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-widest">Contoh Simulasi (Jika Beda Bank):</p>
                                                    <ul className="space-y-1 font-mono text-sm">
                                                        <li className="flex justify-between gap-4">
                                                            <span>Nominal Tagihan:</span>
                                                            <span>{formatCurrency(amount, currency)}</span>
                                                        </li>
                                                        <li className="flex justify-between gap-4 border-b border-amber-100 dark:border-amber-800/50 pb-1.5">
                                                            <span>Fee Admin VA BNI:</span>
                                                            <span>{formatCurrency(adminFee, currency)} +</span>
                                                        </li>
                                                        <li className="flex justify-between gap-4 font-bold text-blue-700 dark:text-blue-400 pt-1.5">
                                                            <span>Total Diinput:</span>
                                                            <span>{formatCurrency(totalWithFee, currency)}</span>
                                                        </li>
                                                    </ul>
                                                </div>
                                                <p className="text-[10px] mt-2 opacity-70 italic leading-snug">
                                                    * Pembayaran menggunakan sesama bank BNI TIDAK perlu menginput nominal fee tambahan. Biaya transfer antar bank dikenakan terpisah oleh bank pengirim.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* ========================================================= */}
                {/* KARTU 2: ORDER SUMMARY (MENGGUNAKAN TEMA FORMS PEMBAYARAN) */}
                {/* ========================================================= */}
                <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden mb-6">
                    {/* Header ala SummaryCard */}
                    <div className="bg-gradient-to-r from-slate-900 to-slate-800 dark:from-slate-950 dark:to-slate-900 p-6 flex justify-between items-center relative overflow-hidden">
                        <div className="absolute -right-4 -top-4 w-16 h-16 bg-white/5 rounded-full blur-xl"></div>
                        <h3 className="font-bold text-xs uppercase tracking-widest text-white opacity-90 flex items-center gap-2 relative z-10">
                            <ListChecks className="w-4 h-4" /> Registration Details
                        </h3>
                    </div>

                    <div className="p-6 md:p-8 bg-slate-50/30 dark:bg-slate-900/50 space-y-6">

                        {/* RINCIAN PAPER KHUSUS PRESENTER (DIPINDAHKAN KE ATAS) */}
                        {invoiceData.is_presenter && invoiceData.submission_title && (
                            <div className="pb-5 border-b border-slate-100 dark:border-slate-800 animate-in fade-in slide-in-from-top-4 duration-500">
                                <span className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
                                    <BookOpen className="w-3.5 h-3.5" /> Paper Information
                                </span>
                                <p className="font-bold text-sm text-slate-900 dark:text-white leading-relaxed">{invoiceData.submission_title}</p>
                                {invoiceData.submission_authors && (
                                    <p className="text-xs text-slate-500 mt-1.5">
                                        <span className="font-medium text-slate-400">Authors:</span> {invoiceData.submission_authors}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Rincian Role & Kategori */}
                        <div className="flex justify-between items-center pb-5 border-b border-slate-100 dark:border-slate-800">
                            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Category & Role</span>
                            <div className="flex gap-2">
                                <span className="font-bold text-blue-700 dark:text-blue-400 uppercase text-[11px] px-2.5 py-1 bg-blue-50 dark:bg-blue-900/40 rounded-md">
                                    {invoiceData.is_presenter ? "Presenter" : "Participant"}
                                </span>
                                {invoiceData.presentation_format && (
                                    <span className="font-bold text-slate-800 dark:text-slate-200 uppercase text-[11px] px-2.5 py-1 bg-slate-100 dark:bg-slate-800 rounded-md">
                                        {invoiceData.presentation_format}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Rincian Track & Package */}
                        <div className="flex flex-col sm:flex-row sm:justify-between gap-2 pb-5 border-b border-slate-100 dark:border-slate-800">
                            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Package / Track</span>
                            <div className="text-left sm:text-right">
                                <span className="font-bold text-sm text-slate-900 dark:text-white block leading-snug">{invoiceData.package_name}</span>
                                <span className="text-xs text-slate-500">{invoiceData.conference_name}</span>
                            </div>
                        </div>

                        {/* Rincian Pendaftar */}
                        <div className="flex flex-col sm:flex-row sm:justify-between gap-2 pb-5 border-b border-slate-100 dark:border-slate-800">
                            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Billed To</span>
                            <div className="text-left sm:text-right">
                                <span className="font-bold text-sm text-slate-900 dark:text-white block">{invoiceData.participant_name}</span>
                                <span className="text-xs text-slate-500 block break-all">{invoiceData.email}</span>
                                <span className="text-xs text-slate-500">{invoiceData.institution_name}</span>
                            </div>
                        </div>

                        {/* Rincian Qty & Resend Email Bawah */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center justify-between sm:block">
                                <span className="text-sm font-medium text-slate-500 dark:text-slate-400 sm:block">Quantity</span>
                                <span className="font-bold text-sm text-slate-900 dark:text-white sm:block sm:mt-1">{invoiceData.quantity} Person(s)</span>
                            </div>

                            {/* Tombol Resend Email (Hanya muncul jika belum lunas dan banner email gagal tidak sedang aktif) */}
                            {status === "unpaid" && bannerState !== "created_failed" && (
                                <div className="flex items-center justify-end border-t border-slate-100 dark:border-slate-800 sm:border-0 pt-4 sm:pt-0">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => resendEmailMutation.mutate()}
                                        disabled={resendEmailMutation.isPending}
                                        className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 w-full sm:w-auto rounded-xl"
                                    >
                                        {resendEmailMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                                        Resend Invoice Email
                                    </Button>
                                </div>
                            )}
                        </div>

                    </div>
                </div>

                {/* ================= PAYMENT GUIDE ================= */}
                {status === "unpaid" && (verification_status === "approved" || verification_status === "not_required") && invoiceData.invoice_number && (
                    <div className="mb-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <PaymentGuide />
                    </div>
                )}

                {/* ================= FOOTER NOTE ================= */}
                <div className="text-center text-xs text-slate-400 dark:text-slate-500 space-y-1 pb-10">
                    {verification_status === 'pending' ? (
                        <p>You can refresh or bookmark this page to check your verification status later.</p>
                    ) : status === 'unpaid' ? (
                        <p>If you have successfully made the payment, the status will be updated automatically.</p>
                    ) : null}
                    <p>For any issues, please contact the committee via WhatsApp.</p>
                </div>
            </div>
        </div>
    );
}